import { useState, useEffect } from "react";
import { useTheme, contactInfo, regions, strategicPartner } from "../data/data";
import { Fade, PageHeader, SectionLabel, SectionTitle, SmartImage } from "../utils/ui";

// === Leadership team photos ===
import founderPhoto      from "../assets/team/founder.jpg";
import directorPhoto     from "../assets/team/director.jpg";
// import managerPhoto      from "../assets/team/manager.jpg";
// import teamManagerPhoto  from "../assets/team/team-manager.jpg";

// ════════════════════════════════════════════════════════════════
// LEADERSHIP TEAM — Edit names / roles / bios / messages here
// "message" is the personal note shown when a visitor clicks the photo.
// ════════════════════════════════════════════════════════════════
const TEAM_MEMBERS = [
  {
    photo: directorPhoto,
    name: "Sourabh Sharma",
    role: "Director",
    bio: "Leading operations and strategic partnerships across our global trade network.",
    message: "As a director, my role is to make sure Kuldiip Enterprises stays true to the values it was built on — integrity, quality, and consistency. I work alongside our team and partners to set our direction, evaluate new markets, and keep our clients at the centre of every decision. Whether we are shipping our hundredth container or our first to a new buyer, the standard does not change.",
  },
  {
    photo: founderPhoto,
    name: "Mohit Mehtaa",
    role: "General Manager",
    bio: "Driving the vision of Kuldiip Enterprises — building trusted global trade relationships from India.",
    message: "As General Manager, my work is about keeping every promise we make to our clients. From the first enquiry to final delivery, my team and I track every detail — coordinating with sourcing partners, logistics teams, and buyers across continents to make sure shipments move on time and to specification. Trade is a relationship business, and I take pride in making sure our clients always feel taken care of.",
  },
  /* {
    photo: managerPhoto,
    name: "Ppramod Patil",
    role: "Relationship & Purchase Manager",
    bio: "Managing client relationships and overseeing procurement to ensure quality and timely delivery.",
    message: "Every client has unique needs — a specific grade, a delivery window, a packaging standard, a price target. My job is to listen carefully and translate those needs into action. I work closely with our trusted sourcing network across India to secure the right product at the right price, and I stay in touch through every step of the order. We are not here to close one deal — we are here to build a long-term relationship you can rely on.",
  },
  {
    photo: teamManagerPhoto,
    name: "Suguresh C",
    role: "Relationship & Team Manager",
    bio: "Building strong client relationships and leading our on-ground team to deliver every order with care.",
    message: "I believe a good trading company is only as strong as the people behind it — and the trust those people earn from their clients. My focus is on two things: keeping our clients heard and our team moving in the same direction. I follow every order personally, from the first call through to the day the goods reach the buyer. When something needs attention, I want our clients to know there is always someone on the other side of the line ready to help.",
  },*/
];

/* ═══════════════════════════════════════════════════════════
   ABOUT
   ═══════════════════════════════════════════════════════════ */
export function About() {
  const { theme } = useTheme();
  const [activeMember, setActiveMember] = useState(null);
  return (
    <div style={{ paddingTop: 70, minHeight: "100vh" }}>
      <PageHeader
        theme={theme}
        tag="Who We Are"
        title="About Kuldiip Enterprises"
        subtitle="A trusted global trading company committed to quality, competitive pricing, and reliable logistics — connecting suppliers and buyers across continents."
        image="https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1600&q=80"
      />

      {/* STORY */}
      <section style={{ padding: "6rem 5vw", background: theme.pageBg, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "5rem", alignItems: "center" }} className="grid-2">
          <Fade>
            <SectionLabel theme={theme}>Our Story</SectionLabel>
            <SectionTitle theme={theme}>Your Reliable Global Trade Partner</SectionTitle>
            <p style={{ fontSize: "0.95rem", color: theme.textSecondary, lineHeight: 1.85, marginBottom: "1.4rem" }}>
              Kuldiip Enterprises is a trusted global trading company engaged in the supply and export of industrial raw materials and agro commodities. We are committed to delivering high-quality products, competitive pricing, and reliable logistics solutions to our international clients.
            </p>
            <p style={{ fontSize: "0.95rem", color: theme.textSecondary, lineHeight: 1.85, marginBottom: "1.4rem" }}>
              Based in Jodhpur, Rajasthan — the heart of India's trading belt — with a branch in Hospet, Karnataka, we leverage deep sourcing networks across the country to bring the finest minerals and agro products to global buyers.
            </p>
            <p style={{ fontSize: "0.95rem", color: theme.textSecondary, lineHeight: 1.85, marginBottom: "2rem" }}>
              Through our strategic partnership with {strategicPartner.name} in Canada, we also extend our reach into North American markets with structured, compliant international transactions.
            </p>
            <div style={{ display: "flex", gap: 20 }}>
              {[{v:"15+",l:"Years Experience"},{v:"20+",l:"Products"},{v:"Global",l:"Partnerships"}].map((s,i) => (
                <div key={i} style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.8rem", fontWeight: 700, color: theme.primary }}>{s.v}</div>
                  <div style={{ fontSize: "0.72rem", color: theme.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginTop: 4 }}>{s.l}</div>
                </div>
              ))}
            </div>
          </Fade>
          <Fade delay={0.18}>
            {/* Photo-led grid instead of plain icon grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[
                { img:"https://images.unsplash.com/photo-1494412519320-aa613dfb7738?auto=format&fit=crop&w=500&q=80", seed:"port",     label:"Global Reach",   icon:"🌍" },
                { img:"https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=500&q=80", seed:"quality",  label:"Quality First",  icon:"✅" },
                { img:"https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=500&q=80",    seed:"b2b",      label:"B2B Focused",    icon:"💼" },
                { img:"https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=500&q=80", seed:"partner",  label:"Trusted Partner",icon:"🤝" },
              ].map((item, i) => (
                <div key={i} style={{
                  position: "relative", height: 165, borderRadius: 12, overflow: "hidden",
                  border: `1px solid ${theme.border}`,
                  boxShadow: theme.shadowCard,
                  transition: "transform 0.3s, box-shadow 0.3s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = theme.shadowHover; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = theme.shadowCard; }}>
                  <SmartImage src={item.img} fallbackSeed={item.seed} alt={item.label} />
                  <div style={{ position: "absolute", inset: 0, background: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(13,76,122,0.85) 100%)` }} />
                  <div style={{ position: "absolute", inset: 0, padding: "1rem", display: "flex", flexDirection: "column", justifyContent: "space-between", color: "#fff" }}>
                    <div style={{ fontSize: "1.8rem" }}>{item.icon}</div>
                    <div style={{ fontSize: "0.92rem", fontWeight: 700 }}>{item.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </Fade>
        </div>
      </section>

      {/* VISION & MISSION (from S&R PDF) */}
      <section style={{ padding: "5rem 5vw", background: theme.sectionAlt, borderTop: `1px solid ${theme.border}`, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Fade style={{ textAlign: "center", marginBottom: "3rem" }}>
            <SectionLabel theme={theme}>What Drives Us</SectionLabel>
            <SectionTitle theme={theme} center>Vision & Mission</SectionTitle>
          </Fade>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem" }} className="grid-2">
            <Fade>
              <div style={{
                background: theme.cardBg, border: `1px solid ${theme.border}`,
                borderRadius: 16, padding: "2.4rem", position: "relative", overflow: "hidden",
                boxShadow: theme.shadowCard, transition: "all 0.3s, background 0.35s",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = theme.shadowHover; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = theme.shadowCard; }}>
                <div style={{ position: "absolute", top: -30, right: -30, width: 140, height: 140, borderRadius: "50%", background: `${theme.accent}12` }} />
                <div style={{ position: "relative" }}>
                  <div style={{ fontSize: "2.4rem", marginBottom: "1rem" }}>👁️</div>
                  <div style={{ fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", color: theme.accent, fontWeight: 700, marginBottom: 10 }}>Vision</div>
                  <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.4rem", fontWeight: 700, color: theme.textPrimary, marginBottom: "1rem", lineHeight: 1.3 }}>
                    A leading global trade partner, connecting continents.
                  </h3>
                  <p style={{ fontSize: "0.9rem", color: theme.textSecondary, lineHeight: 1.85 }}>
                    To be a leading global trade partner, creating successful business connections across continents — bridging suppliers and buyers with consistent integrity.
                  </p>
                </div>
              </div>
            </Fade>
            <Fade delay={0.15}>
              <div style={{
                background: theme.cardBg, border: `1px solid ${theme.border}`,
                borderRadius: 16, padding: "2.4rem", position: "relative", overflow: "hidden",
                boxShadow: theme.shadowCard, transition: "all 0.3s, background 0.35s",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = theme.shadowHover; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = theme.shadowCard; }}>
                <div style={{ position: "absolute", top: -30, right: -30, width: 140, height: 140, borderRadius: "50%", background: `${theme.primary}12` }} />
                <div style={{ position: "relative" }}>
                  <div style={{ fontSize: "2.4rem", marginBottom: "1rem" }}>🎯</div>
                  <div style={{ fontSize: "0.7rem", letterSpacing: "0.18em", textTransform: "uppercase", color: theme.primary, fontWeight: 700, marginBottom: 10 }}>Mission</div>
                  <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.4rem", fontWeight: 700, color: theme.textPrimary, marginBottom: "1rem", lineHeight: 1.3 }}>
                    Smooth, reliable trade with efficiency and integrity.
                  </h3>
                  <p style={{ fontSize: "0.9rem", color: theme.textSecondary, lineHeight: 1.85 }}>
                    To facilitate smooth and reliable trade by leveraging our expertise in supplier–buyer connections, navigating international markets with efficiency and integrity.
                  </p>
                </div>
              </div>
            </Fade>
          </div>
        </div>
      </section>

      {/* LEADERSHIP TEAM */}
      <section style={{ padding: "6rem 5vw", background: theme.sectionAlt, borderTop: `1px solid ${theme.border}`, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <Fade style={{ textAlign: "center", marginBottom: "3.5rem" }}>
            <SectionLabel theme={theme}>Leadership</SectionLabel>
            <SectionTitle theme={theme} center>Meet Our Leadership</SectionTitle>
            <p style={{ fontSize: "0.95rem", color: theme.textSecondary, maxWidth: 600, margin: "0 auto", lineHeight: 1.82 }}>
              Meet the people behind Kuldiip Enterprises — committed to building lasting trade relationships across the globe.
            </p>
          </Fade>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "2rem", maxWidth: 1100, margin: "0 auto" }}>
            {TEAM_MEMBERS.map((m, i) => (
              <Fade key={i} delay={i * 0.12}>
                <div
                  onClick={() => setActiveMember(m)}
                  style={{
                    background: theme.cardBg,
                    borderRadius: 16,
                    overflow: "hidden",
                    boxShadow: theme.shadowCard,
                    border: `1px solid ${theme.border}`,
                    transition: "all 0.3s, background 0.35s",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    cursor: "pointer",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-6px)";
                    e.currentTarget.style.boxShadow = theme.shadowHover;
                    e.currentTarget.style.borderColor = theme.primary + "40";
                    const img = e.currentTarget.querySelector(".team-photo");
                    const overlay = e.currentTarget.querySelector(".team-hover");
                    if (img) img.style.transform = "scale(1.07)";
                    if (overlay) overlay.style.opacity = "1";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = theme.shadowCard;
                    e.currentTarget.style.borderColor = theme.border;
                    const img = e.currentTarget.querySelector(".team-photo");
                    const overlay = e.currentTarget.querySelector(".team-hover");
                    if (img) img.style.transform = "scale(1)";
                    if (overlay) overlay.style.opacity = "0";
                  }}
                >

                  {/* Photo */}
                  <div style={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "4/5",
                    overflow: "hidden",
                    background: theme.sectionAlt,
                  }}>
                    <img
                      className="team-photo"
                      src={m.photo}
                      alt={m.name || m.role}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        transition: "transform 0.6s cubic-bezier(0.22,1,0.36,1)",
                      }}
                    />
                    {/* Top accent stripe */}
                    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: theme.accent, zIndex: 2 }} />

                    {/* Hover overlay with "View" hint */}
                    <div className="team-hover" style={{
                      position: "absolute", inset: 0,
                      background: "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.65) 100%)",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                      display: "flex", alignItems: "flex-end", justifyContent: "center",
                      paddingBottom: 18,
                      pointerEvents: "none",
                    }}>
                      <span style={{
                        background: "rgba(255,255,255,0.95)",
                        color: theme.primary,
                        padding: "0.45rem 1.1rem",
                        borderRadius: 999,
                        fontSize: "0.74rem",
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        boxShadow: "0 4px 14px rgba(0,0,0,0.22)",
                        whiteSpace: "nowrap",
                      }}>
                        🔍 View Profile
                      </span>
                    </div>
                  </div>

                  {/* Text */}
                  <div style={{ padding: "1.5rem 1.5rem 1.7rem", textAlign: "center", flex: 1, display: "flex", flexDirection: "column" }}>
                    <div style={{
                      fontFamily: "'Playfair Display',serif",
                      fontSize: "1.25rem",
                      fontWeight: 700,
                      color: theme.textPrimary,
                      marginBottom: 6,
                      minHeight: "1.5rem",
                    }}>
                      {m.name || <span style={{ color: theme.textMuted, fontStyle: "italic", fontSize: "0.95rem" }}>Name to be added</span>}
                    </div>
                    <div style={{
                      fontSize: "0.7rem",
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      color: theme.accent,
                      fontWeight: 700,
                      marginBottom: 14,
                    }}>
                      {m.role}
                    </div>
                    <div style={{
                      width: 36,
                      height: 2,
                      background: theme.border,
                      margin: "0 auto 14px",
                    }} />
                    <p style={{
                      fontSize: "0.85rem",
                      color: theme.textSecondary,
                      lineHeight: 1.7,
                      marginBottom: "1.2rem",
                      flex: 1,
                    }}>
                      {m.bio}
                    </p>

                    {/* Read message button */}
                    <div style={{
                      fontSize: "0.78rem",
                      fontWeight: 700,
                      color: theme.primary,
                      letterSpacing: "0.04em",
                      paddingTop: "0.8rem",
                      borderTop: `1px solid ${theme.border}`,
                    }}>
                      💬 Read Personal Message →
                    </div>
                  </div>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL PRESENCE (from S&R PDF) */}
      <section style={{ padding: "6rem 5vw", background: theme.pageBg, borderTop: `1px solid ${theme.border}`, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <Fade style={{ textAlign: "center", marginBottom: "3rem" }}>
            <SectionLabel theme={theme}>Global Presence</SectionLabel>
            <SectionTitle theme={theme} center>Active Across Key Markets</SectionTitle>
            <p style={{ fontSize: "0.95rem", color: theme.textSecondary, maxWidth: 560, margin: "0 auto", lineHeight: 1.82 }}>
              We supply to key markets across Africa, the Middle East and Asia — with North American reach through our strategic partner.
            </p>
          </Fade>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2.5rem", alignItems: "center" }} className="grid-2">
            <Fade>
              <div style={{ position: "relative", borderRadius: 16, overflow: "hidden", minHeight: 380, background: theme.sectionAlt, boxShadow: theme.shadowCard }}>
                <SmartImage
                  src="https://images.unsplash.com/photo-1589519160732-57fc498494f8?auto=format&fit=crop&w=1100&q=80"
                  fallbackSeed="worldmap"
                  alt="World map"
                />
                <div style={{ position: "absolute", inset: 0, background: `linear-gradient(135deg, ${theme.primary}33, ${theme.primary}77)` }} />
              </div>
            </Fade>
            <Fade delay={0.15}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {regions.map((r, i) => (
                  <div key={i} style={{
                    background: theme.cardBg, border: `1px solid ${theme.border}`,
                    borderRadius: 12, padding: "1.2rem",
                    transition: "all 0.28s, background 0.35s",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = theme.shadowCard; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}>
                    <div style={{ fontSize: "1.6rem", marginBottom: 6 }}>{r.flag}</div>
                    <div style={{ fontSize: "0.85rem", fontWeight: 700, color: theme.textPrimary, marginBottom: 4 }}>{r.region}</div>
                    <div style={{ fontSize: "0.76rem", color: theme.textMuted, lineHeight: 1.5 }}>{r.countries}</div>
                  </div>
                ))}
              </div>
            </Fade>
          </div>
        </div>
      </section>

      {/* BUSINESS INFO */}
      <section style={{ padding: "5rem 5vw", background: theme.sectionAlt, borderTop: `1px solid ${theme.border}`, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <Fade style={{ textAlign: "center", marginBottom: "3rem" }}>
            <SectionLabel theme={theme}>Company Details</SectionLabel>
            <SectionTitle theme={theme} center>Business Information</SectionTitle>
          </Fade>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "1.4rem" }} className="grid-3">
            {[
              { icon:"📊", title:"Business Type", value:"Export & Supply Trading" },
              { icon:"📍", title:"Registered Office",   value:"Jodhpur, Rajasthan, India" },
              { icon:"🏢", title:"Branch Office", value:"Hospet, Karnataka, India" },
              { icon:"🚢", title:"Trade Terms",   value:"FOB / CIF / CFR" },
              { icon:"📦", title:"Order Type",    value:"Bulk Orders Preferred" },
              { icon:"🌐", title:"Open For",      value:"Global Partnerships & Bulk Orders" },
            ].map((item, i) => (
              <Fade key={i} delay={i * 0.08}>
                <div style={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 12, padding: "1.5rem 1.6rem", display: "flex", alignItems: "center", gap: "1rem", transition: "all 0.28s, background 0.35s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = theme.shadowCard; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}>
                  <div style={{ width: 46, height: 46, background: theme.badgeBg, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem", flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: "0.66rem", letterSpacing: "0.12em", textTransform: "uppercase", color: theme.textMuted, marginBottom: 4, fontWeight: 600 }}>{item.title}</div>
                    <div style={{ fontSize: "0.9rem", fontWeight: 700, color: theme.textPrimary }}>{item.value}</div>
                  </div>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM LIGHTBOX — opens when a member card is clicked */}
      {activeMember && (
        <TeamLightbox member={activeMember} onClose={() => setActiveMember(null)} theme={theme} />
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   TEAM LIGHTBOX — Full-screen profile view with personal message
   ════════════════════════════════════════════════════════════════ */
function TeamLightbox({ member, onClose, theme }) {
  useEffect(() => {
    const handler = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(5,10,20,0.92)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "2rem",
        animation: "teamLightboxFadeIn 0.25s ease-out",
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: "fixed", top: 20, right: 20, zIndex: 10,
          width: 48, height: 48, borderRadius: "50%",
          background: "rgba(255,255,255,0.15)",
          border: "1.5px solid rgba(255,255,255,0.3)",
          color: "#fff", fontSize: "1.5rem", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.22s",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.25)"; e.currentTarget.style.transform = "rotate(90deg)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; e.currentTarget.style.transform = "rotate(0)"; }}
      >✕</button>

      {/* Card */}
      <div
        onClick={e => e.stopPropagation()}
        className="team-lightbox-card"
        style={{
          background: theme.cardBg,
          borderRadius: 16,
          maxWidth: 1000, width: "100%", maxHeight: "92vh",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.5)",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(320px, 1.15fr)",
          animation: "teamLightboxSlideIn 0.3s ease-out",
        }}
      >
        {/* Big photo */}
        <div style={{
          position: "relative",
          background: theme.sectionAlt,
          minHeight: 480,
          overflow: "hidden",
        }}>
          <img
            src={member.photo}
            alt={member.name || member.role}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: theme.accent }} />
        </div>

        {/* Message */}
        <div style={{
          padding: "3rem 2.5rem",
          display: "flex", flexDirection: "column",
          overflowY: "auto",
        }}>
          <div style={{
            fontSize: "0.65rem", letterSpacing: "0.18em", textTransform: "uppercase",
            color: theme.accent, marginBottom: 12, fontWeight: 700,
          }}>
            {member.role}
          </div>

          <h2 style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: "2rem", fontWeight: 700,
            color: theme.textPrimary, marginBottom: "0.4rem", lineHeight: 1.2,
          }}>
            {member.name || <span style={{ color: theme.textMuted, fontStyle: "italic", fontSize: "1.4rem" }}>Name to be added</span>}
          </h2>

          <div style={{
            fontSize: "0.85rem", color: theme.textMuted, marginBottom: "1.6rem",
            fontStyle: "italic",
          }}>
            {member.bio}
          </div>

          <div style={{
            width: 48, height: 3, background: theme.accent,
            marginBottom: "1.6rem", borderRadius: 2,
          }} />

          {/* Personal message with quote styling */}
          <div style={{ position: "relative", paddingLeft: "1.6rem" }}>
            <div style={{
              position: "absolute", left: -4, top: -16,
              fontSize: "4rem", lineHeight: 1,
              color: theme.accent, opacity: 0.25,
              fontFamily: "'Playfair Display',serif",
              fontWeight: 900,
              userSelect: "none",
            }}>
              &ldquo;
            </div>
            <p style={{
              fontSize: "0.96rem",
              color: theme.textSecondary,
              lineHeight: 1.85,
              fontStyle: "italic",
              margin: 0,
            }}>
              {member.message}
            </p>
            <div style={{
              marginTop: "1.4rem",
              paddingTop: "1rem",
              borderTop: `1px solid ${theme.border}`,
              fontSize: "0.78rem",
              color: theme.textMuted,
              letterSpacing: "0.04em",
            }}>
              — {member.name || member.role}
            </div>
          </div>

          {/* CTA */}
          <a
            href="/contact"
            style={{
              display: "block",
              background: theme.accent, color: theme.textOnAccent,
              textAlign: "center", textDecoration: "none",
              padding: "0.9rem 1.5rem", borderRadius: 9,
              fontSize: "0.88rem", fontWeight: 700,
              boxShadow: `0 4px 18px ${theme.accent}45`,
              transition: "all 0.25s",
              marginTop: "auto",
              letterSpacing: "0.02em",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = theme.accentHover; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = theme.accent; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            Get in Touch →
          </a>
        </div>
      </div>

      <style>{`
        @keyframes teamLightboxFadeIn { from {opacity:0} to {opacity:1} }
        @keyframes teamLightboxSlideIn { from {opacity:0; transform: scale(0.96)} to {opacity:1; transform: scale(1)} }
        @media (max-width: 768px) {
          .team-lightbox-card { grid-template-columns: 1fr !important; max-height: 95vh !important; }
          .team-lightbox-card > div:first-child { min-height: 320px !important; }
        }
      `}</style>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   QUALITY
   ═══════════════════════════════════════════════════════════ */
export function Quality() {
  const { theme } = useTheme();
  return (
    <div style={{ paddingTop: 70, minHeight: "100vh" }}>
      <PageHeader
        theme={theme}
        tag="Our Promise"
        title="Quality Assurance"
        subtitle="Strict quality protocols at every step — from sourcing to final delivery at port."
        image="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=1600&q=80"
      />

      {/* 3 PILLARS WITH IMAGES */}
      <section style={{ padding: "6rem 5vw", background: theme.pageBg, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <Fade style={{ textAlign: "center", marginBottom: "3.5rem" }}>
            <SectionLabel theme={theme}>Quality Pillars</SectionLabel>
            <SectionTitle theme={theme} center>Our Three Commitments</SectionTitle>
          </Fade>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "1.8rem" }} className="grid-3">
            {[
              { num:"01", icon:"🏆", title:"High Quality", sub:"Standardized Products", desc:"Every product undergoes rigorous quality checks to meet international import standards. We supply only standardized, certified materials ensuring complete buyer confidence.", color:"#1d6fa8", img:"https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=700&q=80", seed:"qc-lab" },
              { num:"02", icon:"💰", title:"Global Pricing", sub:"Competitive Rates", desc:"Our direct sourcing network enables highly competitive pricing for bulk orders. Transparent quotes with no hidden charges — giving our international partners the best value.", color:"#059669", img:"https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=700&q=80", seed:"pricing" },
              { num:"03", icon:"🚢", title:"Supply Chain", sub:"Timely Delivery", desc:"End-to-end logistics from inland sourcing to port of origin. Reliable shipping schedules, proper documentation, and transparent tracking for every single consignment.", color:"#b45309", img:"https://images.unsplash.com/photo-1494412519320-aa613dfb7738?auto=format&fit=crop&w=700&q=80", seed:"shipping" },
            ].map((item, i) => (
              <Fade key={i} delay={i * 0.14}>
                <div style={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 16, overflow: "hidden", transition: "all 0.3s, background 0.35s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = theme.shadowHover; e.currentTarget.style.borderColor = item.color + "50"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = theme.shadowCard; e.currentTarget.style.borderColor = theme.border; }}>
                  <div style={{ position: "relative", height: 160, overflow: "hidden" }}>
                    <SmartImage src={item.img} fallbackSeed={item.seed} alt={item.title} />
                    <div style={{ position: "absolute", inset: 0, background: `linear-gradient(180deg, ${item.color}22, ${item.color}88)` }} />
                    <div style={{ position: "absolute", top: 14, right: 14, fontFamily: "'Playfair Display',serif", fontSize: "2.8rem", fontWeight: 700, color: "rgba(255,255,255,0.88)", lineHeight: 1 }}>{item.num}</div>
                    <div style={{ position: "absolute", bottom: 14, left: 14, fontSize: "2.4rem" }}>{item.icon}</div>
                  </div>
                  <div style={{ padding: "1.6rem 1.8rem 1.8rem" }}>
                    <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.3rem", fontWeight: 700, color: theme.textPrimary, marginBottom: 5 }}>{item.title}</h3>
                    <div style={{ fontSize: "0.7rem", letterSpacing: "0.12em", textTransform: "uppercase", color: item.color, fontWeight: 700, marginBottom: "0.9rem" }}>{item.sub}</div>
                    <p style={{ fontSize: "0.86rem", color: theme.textSecondary, lineHeight: 1.82 }}>{item.desc}</p>
                  </div>
                </div>
              </Fade>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section style={{ padding: "5rem 5vw", background: theme.sectionAlt, borderTop: `1px solid ${theme.border}`, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <Fade style={{ textAlign: "center", marginBottom: "3rem" }}>
            <SectionLabel theme={theme}>How We Work</SectionLabel>
            <SectionTitle theme={theme} center>Our Supply Process</SectionTitle>
          </Fade>
          <Fade>
            <div style={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 16, padding: "3rem 2rem", transition: "background 0.35s" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: "0.5rem" }}>
                {[
                  { step:"01", label:"Sourcing",       icon:"🔍" },
                  { step:"02", label:"Quality Check",  icon:"✅" },
                  { step:"03", label:"Processing",     icon:"⚙️" },
                  { step:"04", label:"Packaging",      icon:"📦" },
                  { step:"05", label:"Shipping",       icon:"🚢" },
                  { step:"06", label:"Delivery",       icon:"✔️" },
                ].map((s, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ textAlign: "center", padding: "0 0.75rem" }}>
                      <div style={{ width: 62, height: 62, borderRadius: "50%", background: theme.badgeBg, border: `2px solid ${theme.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.6rem", margin: "0 auto 10px", transition: "all 0.25s, background 0.35s" }}
                        onMouseEnter={e => { e.currentTarget.style.background = theme.primary; e.currentTarget.style.borderColor = theme.primary; }}
                        onMouseLeave={e => { e.currentTarget.style.background = theme.badgeBg; e.currentTarget.style.borderColor = theme.border; }}>
                        {s.icon}
                      </div>
                      <div style={{ fontSize: "0.62rem", letterSpacing: "0.1em", textTransform: "uppercase", color: theme.primary, fontWeight: 700, marginBottom: 3 }}>{s.step}</div>
                      <div style={{ fontSize: "0.8rem", color: theme.textSecondary, fontWeight: 500 }}>{s.label}</div>
                    </div>
                    {i < 5 && <div style={{ color: theme.accent, fontSize: "1.3rem", flexShrink: 0, padding: "0 0.2rem", marginBottom: 30 }}>→</div>}
                  </div>
                ))}
              </div>
            </div>
          </Fade>
        </div>
      </section>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   CONTACT — 2 phones, 2 addresses
   ═══════════════════════════════════════════════════════════ */
// ================================================================
// IMPORTANT — PUT YOUR EMAIL ADDRESS HERE TO RECEIVE ENQUIRIES
// ================================================================
// All form submissions will be sent to this email via FormSubmit.co
// (free service, no signup needed).
//
// SETUP STEPS:
//   1. Replace the email below with your real email.
//   2. Deploy the site and submit ONE test enquiry from the contact form.
//   3. You'll receive a one-time confirmation email from FormSubmit —
//      click the link inside it to activate your inbox.
//   4. After that, every enquiry goes straight to your inbox.
//
// You can change this email any time; just redeploy after editing.
const CONTACT_EMAIL = "sourabh.dogger@gmail.com";

export function Contact() {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({ name:"", company:"", email:"", phone:"", product:"", message:"" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const validate = () => {
    const e = {};
    if (!formData.name.trim()) e.name = "Name is required";
    if (!formData.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = "Please enter a valid email";
    if (!formData.message.trim()) e.message = "Message is required";
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSending(true);
    setSubmitError("");

    try {
      // Send to FormSubmit.co — delivers to CONTACT_EMAIL above
      const response = await fetch(`https://formsubmit.co/ajax/${CONTACT_EMAIL}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({
          _subject: `New Enquiry from ${formData.name} — Kuldiip Website`,
          _template: "table",
          _captcha: "false",
          Name: formData.name,
          Company: formData.company || "(not provided)",
          Email: formData.email,
          Phone: formData.phone || "(not provided)",
          "Product Interest": formData.product || "(not specified)",
          Message: formData.message,
          "Submitted From": "Kuldiip Enterprises Website - Contact Form",
        }),
      });

      if (response.ok) {
        setSent(true);
      } else {
        setSubmitError("Sorry, there was an error sending your enquiry. Please try again or email us directly.");
      }
    } catch (err) {
      setSubmitError("Network error. Please check your connection and try again, or email us directly.");
    } finally {
      setSending(false);
    }
  };

  const input = (field, label, placeholder, type = "text") => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase", color: theme.textSecondary, marginBottom: 6, fontWeight: 600 }}>{label}</label>
      <input type={type} value={formData[field]} placeholder={placeholder}
        onChange={e => { setFormData(p => ({...p,[field]:e.target.value})); setErrors(p => ({...p,[field]:null})); }}
        style={{ width:"100%", background: theme.pageBg, border: `1.5px solid ${errors[field] ? "#dc2626" : theme.border}`, borderRadius: 8, padding: "0.78rem 1rem", fontSize: "0.88rem", color: theme.textPrimary, fontFamily: "'DM Sans',sans-serif", outline: "none", transition: "border-color 0.22s, background 0.35s" }}
        onFocus={e => e.target.style.borderColor = theme.primary}
        onBlur={e => e.target.style.borderColor = errors[field] ? "#dc2626" : theme.border} />
      {errors[field] && <div style={{ fontSize: "0.72rem", color: "#dc2626", marginTop: 4 }}>{errors[field]}</div>}
    </div>
  );

  return (
    <div style={{ paddingTop: 70, minHeight: "100vh" }}>
      <PageHeader
        theme={theme}
        tag="Reach Out"
        title="Contact Us"
        subtitle="Open for global partnerships and bulk orders. Get in touch for pricing and availability."
        image="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=1600&q=80"
      />

      {/* OFFICE LOCATIONS */}
      <section style={{ padding: "4rem 5vw 2rem", background: theme.pageBg, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <Fade style={{ textAlign: "center", marginBottom: "2.5rem" }}>
            <SectionLabel theme={theme}>Our Offices</SectionLabel>
            <SectionTitle theme={theme} center>Two Locations Across India</SectionTitle>
          </Fade>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }} className="grid-2">
            {contactInfo.addresses.map((addr, i) => {
              const landmark = i === 0
                ? { img: "https://images.unsplash.com/photo-1675080826144-b49581a386f0?auto=format&fit=crop&w=600&q=80", seed: "mehrangarh", name: "Mehrangarh Fort, Jodhpur" }
                : { img: "https://images.unsplash.com/photo-1616606484004-5ef3cc46e39d?auto=format&fit=crop&w=600&q=80", seed: "hampi",      name: "Hampi Heritage, Karnataka" };
              return (
              <Fade key={i} delay={i * 0.12}>
                <div style={{
                  background: theme.cardBg, border: `1px solid ${theme.border}`,
                  borderRadius: 14, overflow: "hidden",
                  boxShadow: theme.shadowCard,
                  transition: "all 0.3s, background 0.35s",
                  display: "grid", gridTemplateColumns: "180px 1fr",
                }}
                  className="office-card"
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = theme.shadowHover; e.currentTarget.style.borderColor = theme.primary + "50"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = theme.shadowCard; e.currentTarget.style.borderColor = theme.border; }}>
                  <div style={{ position: "relative", background: theme.primary, minHeight: 180, overflow: "hidden" }}>
                    <SmartImage
                      src={landmark.img}
                      fallbackSeed={landmark.seed}
                      alt={landmark.name}
                    />
                    {/* Light bottom-gradient for caption legibility */}
                    <div style={{ position: "absolute", inset: 0, background: `linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.75) 100%)` }} />
                    {/* Pin badge top-left */}
                    <div style={{ position: "absolute", top: 10, left: 10, background: "rgba(255,255,255,0.95)", borderRadius: 999, padding: "4px 10px", fontSize: "0.66rem", fontWeight: 700, color: theme.primary, letterSpacing: "0.08em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4, boxShadow: "0 2px 8px rgba(0,0,0,0.25)" }}>
                      📍 {addr.label === "Registered Office" ? "HQ" : "Branch"}
                    </div>
                    {/* Landmark caption bottom */}
                    <div style={{ position: "absolute", bottom: 10, left: 12, right: 12, color: "#fff", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em", lineHeight: 1.3, textShadow: "0 2px 6px rgba(0,0,0,0.8)" }}>
                      {landmark.name}
                    </div>
                  </div>
                  <div style={{ padding: "1.4rem 1.5rem" }}>
                    <div style={{ fontSize: "0.66rem", letterSpacing: "0.14em", textTransform: "uppercase", color: theme.accent, fontWeight: 700, marginBottom: 6 }}>
                      {addr.label}
                    </div>
                    <div style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.15rem", fontWeight: 700, color: theme.textPrimary, marginBottom: 8 }}>
                      {addr.short}
                    </div>
                    <div style={{ fontSize: "0.83rem", color: theme.textSecondary, lineHeight: 1.6 }}>
                      {addr.full}
                    </div>
                  </div>
                </div>
              </Fade>
              );
            })}
          </div>
        </div>
      </section>

      {/* CONTACT DETAILS + FORM */}
      <section style={{ padding: "3rem 5vw 6rem", background: theme.pageBg, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1.35fr", gap: "4rem", alignItems: "start" }} className="grid-2">

          {/* Left info */}
          <Fade>
            <SectionLabel theme={theme}>Get In Touch</SectionLabel>
            <SectionTitle theme={theme}>Start a Partnership</SectionTitle>
            <p style={{ fontSize: "0.93rem", color: theme.textSecondary, lineHeight: 1.82, marginBottom: "2.5rem" }}>
              We welcome bulk orders and long-term trade partnerships from buyers across the globe. Reach us directly for pricing and logistics details.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem" }}>
              {/* Phone card — hidden if phones array is empty */}
              {contactInfo.phones.length > 0 && (
                <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 12, padding: "1.3rem", transition: "all 0.28s, background 0.35s" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = theme.shadowCard; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}>
                  <div style={{ width: 44, height: 44, background: theme.badgeBg, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem", flexShrink: 0 }}>📞</div>
                  <div>
                    <div style={{ fontSize: "0.65rem", letterSpacing: "0.13em", textTransform: "uppercase", color: theme.accent, marginBottom: 5, fontWeight: 700 }}>Phone / WhatsApp</div>
                    <div style={{ fontSize: "0.88rem", color: theme.textPrimary, lineHeight: 1.75, whiteSpace: "pre-line" }}>
                      {contactInfo.phones.join("\n")}
                    </div>
                  </div>
                </div>
              )}

              {/* Business hours */}
              <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 12, padding: "1.3rem", transition: "all 0.28s, background 0.35s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = theme.shadowCard; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}>
                <div style={{ width: 44, height: 44, background: theme.badgeBg, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem", flexShrink: 0 }}>🕘</div>
                <div>
                  <div style={{ fontSize: "0.65rem", letterSpacing: "0.13em", textTransform: "uppercase", color: theme.accent, marginBottom: 5, fontWeight: 700 }}>Business Hours</div>
                  <div style={{ fontSize: "0.88rem", color: theme.textPrimary, lineHeight: 1.75, whiteSpace: "pre-line" }}>
                    {contactInfo.hours}
                  </div>
                </div>
              </div>

              {/* Global partnerships */}
              <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 12, padding: "1.3rem", transition: "all 0.28s, background 0.35s" }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = theme.shadowCard; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}>
                <div style={{ width: 44, height: 44, background: theme.badgeBg, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.3rem", flexShrink: 0 }}>🌐</div>
                <div>
                  <div style={{ fontSize: "0.65rem", letterSpacing: "0.13em", textTransform: "uppercase", color: theme.accent, marginBottom: 5, fontWeight: 700 }}>Open For</div>
                  <div style={{ fontSize: "0.88rem", color: theme.textPrimary, lineHeight: 1.75 }}>
                    Global Partnerships & Bulk Orders
                  </div>
                </div>
              </div>
            </div>
          </Fade>

          {/* Form */}
          <Fade delay={0.18}>
            <div style={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 16, padding: "2.5rem", boxShadow: theme.shadowHover, transition: "background 0.35s, border-color 0.35s" }}>
              {sent ? (
                <div style={{ textAlign: "center", padding: "3rem 1rem" }}>
                  <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#d1fae5", border: "2px solid #059669", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.2rem", fontSize: "2rem" }}>✅</div>
                  <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.6rem", color: theme.textPrimary, marginBottom: 10 }}>Enquiry Sent!</h3>
                  <p style={{ fontSize: "0.9rem", color: theme.textSecondary, lineHeight: 1.7 }}>Thank you for reaching out. We'll get back to you within 24 hours with pricing and details.</p>
                  <button onClick={() => { setSent(false); setSubmitError(""); setFormData({ name:"",company:"",email:"",phone:"",product:"",message:"" }); }} style={{ marginTop: 20, background: "none", border: `1.5px solid ${theme.border}`, borderRadius: 8, padding: "0.6rem 1.4rem", cursor: "pointer", fontSize: "0.85rem", color: theme.textSecondary, fontFamily: "'DM Sans',sans-serif", transition: "all 0.22s" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = theme.primary}
                    onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}>
                    Send Another Enquiry
                  </button>
                </div>
              ) : (
                <>
                  <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.5rem", fontWeight: 700, color: theme.textPrimary, marginBottom: 6 }}>Request a Quote</h3>
                  <p style={{ fontSize: "0.8rem", color: theme.textMuted, marginBottom: "1.8rem" }}>Fill in your requirements and we'll respond promptly.</p>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {input("name", "Full Name *", "Your name")}
                    {input("company", "Company", "Company name")}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {input("email", "Email *", "you@company.com", "email")}
                    {input("phone", "Phone / WhatsApp", "+91 xxx xxx xxxx", "tel")}
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <label style={{ display: "block", fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase", color: theme.textSecondary, marginBottom: 6, fontWeight: 600 }}>Product Interest</label>
                    <select value={formData.product} onChange={e => setFormData(p => ({...p,product:e.target.value}))} style={{ width:"100%", background: theme.pageBg, border: `1.5px solid ${theme.border}`, borderRadius: 8, padding: "0.78rem 1rem", fontSize: "0.88rem", color: formData.product ? theme.textPrimary : theme.textMuted, fontFamily: "'DM Sans',sans-serif", outline: "none", transition: "border-color 0.22s, background 0.35s" }}
                      onFocus={e => e.target.style.borderColor = theme.primary}
                      onBlur={e => e.target.style.borderColor = theme.border}>
                      <option value="">Select a product...</option>
                      <optgroup label="⚙️ Industrial Raw Materials">{["Iron Ore","Sponge Iron","Iron Pellets","Coal","Bauxite","Quartz"].map(v => <option key={v}>{v}</option>)}</optgroup>
                      <optgroup label="🌾 Agro Commodities">{["IR64 Parboiled Rice","IR64 White Rice","Indian Sugar (M30 & S30)"].map(v => <option key={v}>{v}</option>)}</optgroup>
                      <optgroup label="🌶️ Export Spices">{["Turmeric & Red Chilli","Cumin & Coriander Seeds","Black Pepper & Cardamom","Cloves"].map(v => <option key={v}>{v}</option>)}</optgroup>
                    </select>
                  </div>

                  <div style={{ marginBottom: 22 }}>
                    <label style={{ display: "block", fontSize: "0.7rem", letterSpacing: "0.1em", textTransform: "uppercase", color: theme.textSecondary, marginBottom: 6, fontWeight: 600 }}>Message / Quantity Required *</label>
                    <textarea value={formData.message} onChange={e => { setFormData(p => ({...p,message:e.target.value})); setErrors(p => ({...p,message:null})); }} rows={4}
                      placeholder="Tell us about your requirements, quantity needed, destination port..."
                      style={{ width:"100%", background: theme.pageBg, border: `1.5px solid ${errors.message ? "#dc2626" : theme.border}`, borderRadius: 8, padding: "0.78rem 1rem", fontSize: "0.88rem", color: theme.textPrimary, fontFamily: "'DM Sans',sans-serif", outline: "none", resize: "vertical", minHeight: 100, transition: "border-color 0.22s, background 0.35s" }}
                      onFocus={e => e.target.style.borderColor = theme.primary}
                      onBlur={e => e.target.style.borderColor = errors.message ? "#dc2626" : theme.border} />
                    {errors.message && <div style={{ fontSize: "0.72rem", color: "#dc2626", marginTop: 4 }}>{errors.message}</div>}
                  </div>

                  <button onClick={handleSubmit} disabled={sending} style={{ width:"100%", background: sending ? theme.textMuted : theme.accent, color: theme.textOnAccent, border: "none", padding: "1rem", fontSize: "0.92rem", fontWeight: 700, fontFamily: "'DM Sans',sans-serif", borderRadius: 9, cursor: sending ? "wait" : "pointer", boxShadow: `0 4px 18px ${theme.accent}45`, transition: "all 0.25s", opacity: sending ? 0.7 : 1 }}
                    onMouseEnter={e => { if (!sending) { e.currentTarget.style.background = theme.accentHover; e.currentTarget.style.transform = "translateY(-2px)"; } }}
                    onMouseLeave={e => { if (!sending) { e.currentTarget.style.background = theme.accent; e.currentTarget.style.transform = "translateY(0)"; } }}>
                    {sending ? "Sending..." : "Send Enquiry →"}
                  </button>

                  {submitError && (
                    <div style={{ marginTop: 14, padding: "0.8rem 1rem", background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, color: "#991b1b", fontSize: "0.82rem", lineHeight: 1.5 }}>
                      ⚠️ {submitError}
                    </div>
                  )}
                </>
              )}
            </div>
          </Fade>
        </div>
      </section>
    </div>
  );
}
